import api from './api';

export interface TecnicoReadDto {
  codigoTecnico: string;
  descripcion: string;
}

export interface TecnicoSearchParams {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface TecnicoPagedResult {
  items: TecnicoReadDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateTecnicoDto {
  codigoTecnico: string;
  descripcion: string;
}

export interface UpdateTecnicoDto {
  descripcion: string;
}

const normalizeTecnico = (raw: Record<string, unknown>): TecnicoReadDto => ({
  codigoTecnico: String(raw.codigoTecnico ?? raw.CodigoTecnico ?? '').trim(),
  descripcion: String(raw.descripcion ?? raw.Descripcion ?? '').trim(),
});

const tecnicoService = {
  getTecnicos: async (params: TecnicoSearchParams): Promise<TecnicoPagedResult> => {
    const resp = await api.get('/api/Tecnicos', {
      params: {
        SearchTerm: params.searchTerm || undefined,
        PageNumber: params.pageNumber ?? 1,
        PageSize: params.pageSize ?? 10,
      },
    });
    const data = resp.data;
    if (data && typeof data === 'object' && 'items' in data) {
      const paged = data as TecnicoPagedResult & { Items?: unknown[] };
      const items = (paged.items ?? paged.Items ?? []) as unknown[];
      return {
        items: items.map((item) => normalizeTecnico(item as Record<string, unknown>)),
        totalCount: paged.totalCount ?? (data as { TotalCount?: number }).TotalCount ?? 0,
        pageNumber: paged.pageNumber ?? (data as { PageNumber?: number }).PageNumber ?? 1,
        pageSize: paged.pageSize ?? (data as { PageSize?: number }).PageSize ?? 10,
        totalPages: paged.totalPages ?? (data as { TotalPages?: number }).TotalPages ?? 0,
      };
    }
    return {
      items: [],
      totalCount: 0,
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 10,
      totalPages: 0,
    };
  },

  getTecnico: async (codigoTecnico: string): Promise<TecnicoReadDto> => {
    const encoded = encodeURIComponent(codigoTecnico.trim());
    const resp = await api.get(`/api/Tecnicos/${encoded}`);
    return normalizeTecnico(resp.data as Record<string, unknown>);
  },

  createTecnico: async (dto: CreateTecnicoDto): Promise<TecnicoReadDto> => {
    const response = await api.post('/api/Tecnicos', dto);
    return normalizeTecnico(response.data as Record<string, unknown>);
  },

  updateTecnico: async (codigoTecnico: string, dto: UpdateTecnicoDto): Promise<void> => {
    const encoded = encodeURIComponent(codigoTecnico.trim());
    await api.put(`/api/Tecnicos/${encoded}`, dto);
  },

  deleteTecnico: async (codigoTecnico: string): Promise<void> => {
    const encoded = encodeURIComponent(codigoTecnico.trim());
    await api.delete(`/api/Tecnicos/${encoded}`);
  },
};

export default tecnicoService;
